import type { MutagenId, NormalizedHoroscope, NormalizedHoroscopeItem, StarId } from './domain.js';

export type CoreTimingScope = 'decadal' | 'minor' | 'yearly';

export interface TimingMutagenConvergenceFact {
  id: string;
  mutagen: MutagenId;
  starId: StarId;
  starLabel: string;
  scopes: readonly CoreTimingScope[];
  layerCount: number;
}

export interface TimingCoreConvergenceSummary {
  model: 'TIMING-CORE-CONVERGENCE-0.1.0';
  facts: readonly TimingMutagenConvergenceFact[];
  repeatedFactCount: number;
  maxLayerCount: number;
  disclosure: string;
}

const CORE_SCOPES: readonly CoreTimingScope[] = Object.freeze(['decadal', 'minor', 'yearly']);
const MUTAGEN_ORDER: readonly MutagenId[] = Object.freeze(['lu', 'quan', 'ke', 'ji']);
const MUTAGEN_LABELS: Readonly<Record<MutagenId, string>> = Object.freeze({ lu: '祿', quan: '權', ke: '科', ji: '忌' });
const SCOPE_LABELS: Readonly<Record<CoreTimingScope, string>> = Object.freeze({ decadal: '大限', minor: '小限', yearly: '流年' });

function itemForScope(horoscope: NormalizedHoroscope, scope: CoreTimingScope): NormalizedHoroscopeItem {
  return horoscope[scope];
}

export function buildTimingCoreConvergence(horoscope: NormalizedHoroscope): TimingCoreConvergenceSummary {
  const seen = new Map<string, { mutagen: MutagenId; starId: StarId; starLabel: string; scopes: CoreTimingScope[] }>();
  for (const scope of CORE_SCOPES) {
    for (const entry of itemForScope(horoscope, scope).mutagens) {
      const key = `${entry.mutagen}:${entry.starId}`;
      const current = seen.get(key);
      if (current) current.scopes.push(scope);
      else seen.set(key, { mutagen: entry.mutagen, starId: entry.starId, starLabel: entry.starLabel, scopes: [scope] });
    }
  }
  const facts = [...seen.values()]
    .filter((item) => item.scopes.length >= 2)
    .map((item): TimingMutagenConvergenceFact => Object.freeze({
      id: `timing.core_convergence.mutagen.${item.mutagen}.star.${item.starId}`,
      mutagen: item.mutagen,
      starId: item.starId,
      starLabel: item.starLabel,
      scopes: Object.freeze([...item.scopes]),
      layerCount: item.scopes.length,
    }))
    .sort((a, b) => MUTAGEN_ORDER.indexOf(a.mutagen) - MUTAGEN_ORDER.indexOf(b.mutagen) || a.starId.localeCompare(b.starId));
  return Object.freeze({
    model: 'TIMING-CORE-CONVERGENCE-0.1.0' as const,
    facts: Object.freeze(facts),
    repeatedFactCount: facts.length,
    maxLayerCount: facts.reduce((max, item) => Math.max(max, item.layerCount), 0),
    disclosure: '只列出大限、小限、流年中相同「四化＋星曜」跨兩層以上重複的客觀結構；重複層數不是吉凶分數、準確率、事件機率或結果預測。',
  });
}

export interface TimingMutagenConvergenceFactViewModel {
  id: string;
  mutagen: MutagenId;
  mutagenLabel: string;
  starId: StarId;
  starLabel: string;
  layerCount: number;
  scopeLabels: readonly string[];
}

export interface TimingCoreConvergenceViewModel {
  model: 'TIMING-CORE-CONVERGENCE-VIEW-0.1.0';
  repeatedFactCount: number;
  maxLayerCount: number;
  facts: readonly TimingMutagenConvergenceFactViewModel[];
  emptyState?: string;
  disclosure: string;
}

export function timingCoreConvergenceViewModel(horoscope: NormalizedHoroscope): TimingCoreConvergenceViewModel {
  const summary = buildTimingCoreConvergence(horoscope);
  return Object.freeze({
    model: 'TIMING-CORE-CONVERGENCE-VIEW-0.1.0' as const,
    repeatedFactCount: summary.repeatedFactCount,
    maxLayerCount: summary.maxLayerCount,
    facts: Object.freeze(summary.facts.map((item) => Object.freeze({
      id: item.id,
      mutagen: item.mutagen,
      mutagenLabel: MUTAGEN_LABELS[item.mutagen],
      starId: item.starId,
      starLabel: item.starLabel,
      layerCount: item.layerCount,
      scopeLabels: Object.freeze(item.scopes.map((scope) => SCOPE_LABELS[scope])),
    }))),
    ...(summary.facts.length === 0 ? { emptyState: '目前大限、小限、流年之間沒有相同「四化＋星曜」跨兩層以上重複。' } : {}),
    disclosure: summary.disclosure,
  });
}
